import { useCallback, useMemo, useEffect } from "react";
import { Grid, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material";
import { PolicyResource } from "@vmosolutions/oc-interfaces";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { getResourceFieldPermissions } from "src/API/helpers";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { CopyToClipboard } from "src/pages/TimelineEventEditors/helpers";
import { IFlightRow } from "src/pages/ContentManagement/ReviewContentWindow";

import { AircraftGroupSelectField } from "./Fields/AircraftGroupSelector";
import { AircraftSelectField } from "./Fields/AircraftSelectField";
import { AircraftTypeSelectField } from "./Fields/AircraftTypeSelectField";
import { AirportSelectField } from "./Fields/AirportSelectField";
import { CalculatedCustomField } from "./Fields/CalculatedCustomField";
import { CrewBaseSelectField } from "./Fields/CrewBaseSelectField";
import { CrewGroupSelectField } from "./Fields/CrewGroupSelectField";
import { CrewPositionSelectField } from "./Fields/CrewPositionSelectField";
import { CrewSelectField } from "./Fields/CrewSelectField";
import { HotelSelectField } from "./Fields/HotelSelectField";
import { NonFlyingTypeSelectField } from "./Fields/NonFlyingTypeSelectField";
import { ATASelector } from "./Fields/ATASelector";
import { CustomerSelectField } from "./Fields/CustomerSelector";
import { DelayCategorySelector } from "./Fields/DelayCategorySelector";
import { DelayCodeSelector } from "./Fields/DelayCodeSelector";
import { FlightFlagSelectField } from "./Fields/FlightFlagsSelector";
import { FlightTypeSelectField } from "./Fields/FlightTypeSelector";
import { LogicModifierSelectField } from "./Fields/LogicModifierSelectField";

import {
  FormikCheckboxField,
  FormikTextField,
  FormikSelectField,
  FormikDateTimeField,
  FormikYearField,
} from "../FormikComponents/FormikWrappers";
import { isEqual, set } from "lodash";
import { unflattenObj } from "src/utils/index";
import {
  computeCustomFieldErrors,
  IExtendedJSONSchema6,
  IExtendedJSONSchema6Definition,
} from "./customFieldValidation";

// The schema types and the AJV validation live in ./customFieldValidation (pure logic,
// unit tested on its own). Re-exported here so existing imports keep working.
export type {
  CustomFieldSanitizer,
  ExtendedTypes,
  IExtendedJSONSchema6,
  IExtendedJSONSchema6Definition,
} from "./customFieldValidation";
export { computeCustomFieldErrors, sanitizeCustomValues } from "./customFieldValidation";

const customFieldsStyles = (theme: Theme, isLogsheet = false): any => ({
  ...(isLogsheet ? {} : { margin: "0px 10px" }),
});

const customHeaderStyles = (theme: Theme): any => ({
  margin: "10px 0px 0px 0px",
  fontWeight: 300,
  fontSize: 10,
  textTransform: "uppercase",
  color: theme.custom.forms.labels,
});

const clipboardStyles = (theme: Theme): any => ({
  "& .copyToClipboard": {
    margin: 0,
    width: "20px !important",
    height: "20px !important",
    opacity: 0.25,
    "&:hover": { opacity: 0.75 },
  },
});

const sanitizeProps = (obj: any) => {
  if (!obj) return obj;
  const { active, value, ...safe } = obj;
  return safe;
};

type SelectOption = { label: string; value: string };
type RawSelectOption = string | SelectOption;

interface ICustomFieldsProps {
  schema: IExtendedJSONSchema6 | null;
  objectPath?: string;
  mosaicPane?: HTMLElement;
  resource?: PolicyResource;
  logSheetCustomFields?: IFlightRow[];
  logsheetDefaultValueStyles?: any;
  displayHeader?: boolean;
  labelWidth?: number;
  labelAlignment?: "left" | "right" | "center" | "justify" | "initial" | "inherit";
  onlyValidateOnTouched?: boolean;
}

export const CustomFieldsV2 = (props: ICustomFieldsProps): JSX.Element => {
  const {
    schema,
    mosaicPane,
    resource,
    objectPath,
    displayHeader,
    labelWidth,
    labelAlignment,
    onlyValidateOnTouched,
    logSheetCustomFields,
    logsheetDefaultValueStyles,
  } = props;

  const theme = useTheme();
  const formik = useFormikContext<any>();
  const propertyPath = useMemo(() => (objectPath ? `${objectPath}.` : ""), [objectPath]);
  const itemWidth = labelWidth ? "100%" : 263;
  const defaultLabelSx = {
    ...(labelWidth ? { width: `${labelWidth}px !important` } : {}),
    ...(labelAlignment ? { textAlign: `${labelAlignment} !important` } : {}),
  };
  // Logsheet-specific styles support (match by schema key relative to objectPath)
  const getLogsheetStyles = useCallback(
    (fullPath: string) => {
      const relativePath =
        propertyPath && fullPath.startsWith(propertyPath) ? fullPath.slice(propertyPath.length) : fullPath;
      const normalizedPath = relativePath.replace(/^custom\./, "");
      const format = logSheetCustomFields?.find((field) => {
        const fieldKey = field.key?.replace(/^custom\./, "");
        return fieldKey === normalizedPath;
      });
      const styles = { ...(logsheetDefaultValueStyles || {}), ...(format?.valueStyles || {}) };
      if (format?.width) styles.width = format.width;
      return { styles, exists: !!format };
    },
    [logSheetCustomFields, logsheetDefaultValueStyles, propertyPath]
  );

  const userPolicies = useSelector((state: IRootState) => state.authentication.attributes.policies);

  const checkFieldPermissionFunction = useMemo(
    () => getResourceFieldPermissions(userPolicies, resource),
    [resource, userPolicies]
  );

  const hasFieldPermissions = useCallback(
    (fieldName: string) => (resource ? checkFieldPermissionFunction(fieldName) : true),
    [checkFieldPermissionFunction, resource]
  );

  const isSchemaObject = (s: any): s is IExtendedJSONSchema6Definition => {
    return s && typeof s === "object" && !Array.isArray(s);
  };

  const getCustomFieldErrors = useCallback(
    (customValues: any): Record<string, any> => computeCustomFieldErrors(schema, customValues, propertyPath),
    [schema, propertyPath]
  );

  // Current values at objectPath or root
  const values = useMemo(() => {
    const v = formik?.values ?? {};
    return objectPath ? (get(v, objectPath) ?? {}) : v;
  }, [formik?.values, objectPath]);

  // Touched detection for conditional validation
  const customFieldsTouched = useMemo(() => {
    if (!objectPath) return false;
    const touched = get(formik?.touched, objectPath);
    return !!touched && !!Object.keys(touched || {}).length;
  }, [objectPath, formik?.touched]);

  const customFieldErrors = useMemo(() => getCustomFieldErrors(values), [values, getCustomFieldErrors]);

  useEffect(() => {
    if (!onlyValidateOnTouched || (customFieldsTouched && onlyValidateOnTouched)) {
      // Convert flat AJV paths into nested Formik-compatible object
      const nestedErrors = unflattenObj(customFieldErrors);

      // Merge with existing Formik errors without overwriting unrelated sections
      const newErrors = {
        ...(formik.errors || {}),
        ...nestedErrors,
      };

      // Prevent unnecessary rerenders
      if (!isEqual(formik.errors, newErrors)) {
        formik.setErrors(newErrors);
      }
    }
  }, [customFieldErrors, customFieldsTouched, onlyValidateOnTouched]);

  const renderCommonProps = (schema: any, property: string, isEdit: boolean, hideLabel = false, className?: string) => {
    const selectorProps = sanitizeProps(schema?.selectorProps || {});
    return {
      mosaicPane,
      name: property,
      label: hideLabel ? "" : schema.title,
      ...(className ? { className } : {}),
      disabled: !hasFieldPermissions(property) || (schema.readOnly && isEdit) || schema.readOnly,
      labelSx: defaultLabelSx,
      // Standard select defaults for consistency across portals
      menuPosition: "fixed",
      menuPortalTarget: mosaicPane,
      isSearchable: true,
      isClearable: schema.isClearable ?? false,
      ...selectorProps,
    };
  };

  const renderObjectType = (schema: any, property: string, isEdit: boolean) => {
    const { styles: logStyles, exists: hasLogsheet } = getLogsheetStyles(property);
    const commonProps = renderCommonProps(
      schema,
      property,
      isEdit,
      hasLogsheet,
      hasLogsheet ? "field selectfield" : undefined
    );
    const fieldWrapperStyles = {
      ...(hasLogsheet ? logStyles : { width: itemWidth }),
      ...customFieldsStyles(theme, hasLogsheet),
    };
    const unitOptions: SelectOption[] =
      schema?.properties?.unit?.enum?.map((u: string) => ({ label: u, value: u })) ??
      schema?.properties?.unit?.options?.map((o: RawSelectOption) =>
        typeof o === "string" ? { label: o, value: o } : { label: o.label, value: o.value }
      ) ??
      [];

    const map: Record<string, JSX.Element> = {
      UnitValue: (
        <Grid
          container
          key={property}
          className={hasLogsheet ? "unitvalue" : undefined}
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item xs="auto" style={ hasLogsheet ? { minWidth: 0, paddingRight: 4, flex: "1 1 60%", maxWidth: "60%" } : { paddingRight: 6 } }>
            <FormikTextField
              className={hasLogsheet ? "field textfield custom_value no_arrows" : undefined}
              name={`${property}.value`}
              label={hasLogsheet ? "" : schema.title}
              type="number"
              parse={(v: string) => (isNaN(parseFloat(v)) ? null : parseFloat(v))}
              inputProps={hasLogsheet ? { style: { textAlign: "center" } } : undefined}
              labelSx={defaultLabelSx}
              disabled={!hasFieldPermissions(`${property}.value`) || (schema.readOnly && isEdit) || schema.readOnly}
            />
          </Grid>
          <Grid item xs="auto" style={hasLogsheet ? { minWidth: 0, flex: "0 0 40%", maxWidth: "40%" } : undefined}>
            {hasLogsheet ? (
              <Grid
                item
                className="field textfield custom_unit"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <select
                  name={`${property}.unit`}
                  aria-label={schema.title || "Unit"}
                  value={get(formik.values, `${property}.unit`) ?? unitOptions[0]?.value ?? ""}
                  onChange={(event) => formik.setFieldValue(`${property}.unit`, event.target.value, true)}
                  onBlur={() => formik.setFieldTouched(`${property}.unit`, true, false)}
                  disabled={!hasFieldPermissions(`${property}.unit`) || (schema.readOnly && isEdit) || schema.readOnly}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    color: "inherit",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "20px",
                    textAlign: "center",
                    textAlignLast: "center",
                    padding: "0px 2px",
                    outline: "none",
                  }}
                >
                  {unitOptions.map((option: { label: string; value: string }) => (
                    <option key={`${property}_unit_${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Grid>
            ) : (
              <FormikSelectField
                name={`${property}.unit`}
                label=""
                id={`${property}-unit-select`}
                options={unitOptions}
                  format={(value: string | null | undefined) => unitOptions.find((option: SelectOption) => option.value === value)}
                  parse={(value: SelectOption | null | undefined) => value?.value ?? null}
                menuPosition="fixed"
                menuPortalTarget={mosaicPane}
                isSearchable
                isClearable={schema.isClearable ?? false}
                labelSx={defaultLabelSx}
                disabled={!hasFieldPermissions(`${property}.unit`) || (schema.readOnly && isEdit) || schema.readOnly}
              />
            )}
          </Grid>
        </Grid>
      ),
      Dropdown: (
        <FormikSelectField
          className={hasLogsheet ? "field selectfield" : undefined}
          name={property}
          label={schema.title}
          id={property}
          options={schema?.options || []}
          format={(val: any) => (schema?.options || []).find((o: any) => o.value === val)}
          parse={(val: any) => val?.value ?? null}
          getOptionLabel={(opt: { value: string; label: string }) => `${opt.label} (${opt.value})`}
          menuPosition="fixed"
          menuPortalTarget={mosaicPane}
          isSearchable
          labelSx={defaultLabelSx}
          isClearable={schema.isClearable ?? false}
          disabled={!hasFieldPermissions(property) || (schema.readOnly && isEdit) || schema.readOnly}
        />
      ),
      Date: (
        <FormikDateTimeField
          className={hasLogsheet ? "field datefield" : undefined}
          name={property}
          label={hasLogsheet ? "" : schema.title}
          labelStyle={defaultLabelSx}
          isEpoch={true}
          hideTimeControl={schema.hideTimeControl}
          parse={checkDate(
            property,
            formik.setFieldError,
            schema?.minFieldName ? get(formik.values, schema.minFieldName) : undefined,
            schema?.maxFieldName ? get(formik.values, schema.maxFieldName) : undefined,
            schema?.dateRangeLimit,
            schema?.limitErrorMessage
          )}
          disabled={!hasFieldPermissions(property) || (schema.readOnly && isEdit) || schema.readOnly}
        />
      ),
      Year: <FormikYearField {...commonProps} />,
      OC_CrewMembers: <CrewSelectField {...commonProps} />,
      OC_Airports: <AirportSelectField {...commonProps} />,
      OC_CrewPositions: <CrewPositionSelectField {...commonProps} />,
      OC_CrewGroups: <CrewGroupSelectField {...commonProps} />,
      OC_CrewBases: <CrewBaseSelectField {...commonProps} />,
      OC_Aircraft: <AircraftSelectField {...commonProps} />,
      OC_AircraftTypes: <AircraftTypeSelectField {...commonProps} />,
      OC_AircraftGroups: <AircraftGroupSelectField {...commonProps} />,
      OC_Hotels: <HotelSelectField {...commonProps} />,
      OC_NonFlyingTypes: <NonFlyingTypeSelectField {...commonProps} />,
      OC_FlightTypes: <FlightTypeSelectField {...commonProps} />,
      OC_Customers: <CustomerSelectField {...commonProps} />,
      OC_DelayCodes: <DelayCodeSelector {...commonProps} />,
      OC_DelayCategories: <DelayCategorySelector {...commonProps} />,
      OC_ATA: <ATASelector {...commonProps} />,
      OC_FlightFlags: <FlightFlagSelectField {...commonProps} />,
      OC_LogicModifiers: <LogicModifierSelectField {...commonProps} />,
      OC_Calculated: (
        <CalculatedCustomField
          propertyName={property}
          label={hasLogsheet ? "" : schema.title}
          evalString={schema.evalString}
          evalFields={schema.evalFields}
          formValues={formik.values}
          labelSx={defaultLabelSx}
        />
      ),
    };

    return (
      <Grid item key={property} style={fieldWrapperStyles}>
        {map[schema.objectType]}
      </Grid>
    );
  };

  const renderPrimitive = (schema: any, property: string, value: any, isEdit: boolean) => {
    const disabled = !hasFieldPermissions(property) || (schema.readOnly && isEdit) || schema.readOnly;
    const { styles: logStyles, exists: hasLogsheet } = getLogsheetStyles(property);
    const fieldWrapperStyles = {
      ...(hasLogsheet ? logStyles : { width: itemWidth }),
      ...customFieldsStyles(theme, hasLogsheet),
    };
    const fieldLabel = hasLogsheet ? "" : schema.title;

    switch (schema.type) {
      case "boolean":
        return (
          <Grid key={property} item style={fieldWrapperStyles}>
            <FormikCheckboxField
              className={hasLogsheet ? "field logsheetCheck" : undefined}
              name={property}
              checked={!!value}
              label={fieldLabel}
              isDisabled={disabled}
            />
          </Grid>
        );

      case "number":
      case "integer":
        return (
          <Grid key={property} container alignItems="center" style={fieldWrapperStyles} sx={clipboardStyles(theme)}>
            <FormikTextField
              className={hasLogsheet ? "field textfield no_arrows" : undefined}
              name={property}
              label={fieldLabel}
              type="number"
              parse={(v: string) => (!isNaN(parseFloat(v)) ? parseFloat(v) : null)}
              placeholder={schema.placeholder ?? ""}
              inputProps={hasLogsheet ? { style: { textAlign: "center" } } : undefined}
              labelSx={defaultLabelSx}
              disabled={disabled}
            />
            {schema.readOnly && typeof value === "number" && (
              <CopyToClipboard copyValue={String(value)} className="copyToClipboard" />
            )}
          </Grid>
        );

      default:
        return (
          <Grid key={property} container alignItems="center" style={fieldWrapperStyles} sx={clipboardStyles(theme)}>
            <FormikTextField
              className={hasLogsheet ? "field textfield" : undefined}
              name={property}
              label={fieldLabel}
              disabled={disabled}
              placeholder={schema.placeholder ?? ""}
              inputProps={hasLogsheet ? { style: { textAlign: "center" } } : undefined}
              labelSx={defaultLabelSx}
              multiline={schema.multiline ?? (typeof value === "string" && value?.length > 100)}
              rows={schema.rows ?? (typeof value === "string" && value?.length > 100 ? 3 : undefined)}
            />
            {schema.readOnly && typeof value === "string" && (
              <CopyToClipboard copyValue={value} className="copyToClipboard" />
            )}
          </Grid>
        );
    }
  };

  const formElements = useMemo(() => {
    const elements: JSX.Element[] = [];
    if (!schema?.properties) return elements;

    const isEdit = formik?.values?._id && !formik?.values?._id.includes("temp");

    const isUnitValueShape = (s: any): boolean => {
      return s?.type === "object" && s?.properties?.value && s?.properties?.unit;
    };

    const walkObject = (baseSchema: IExtendedJSONSchema6Definition, basePath: string) => {
      if (isUnitValueShape(baseSchema)) {
        baseSchema.objectType = "UnitValue";
        elements.push(renderObjectType(baseSchema, basePath, isEdit));
        return;
      }

      for (const [childKey, childSchema] of Object.entries(baseSchema.properties || {})) {
        const fullPath = `${basePath}.${childKey}`;
        const value = get(formik?.values, fullPath);
        const field = childSchema;

        if (field?.type === "object" && field?.properties) {
          walkObject(field, fullPath);
          continue;
        }

        if (field?.objectType) {
          elements.push(renderObjectType(field, fullPath, isEdit));
        } else {
          elements.push(renderPrimitive(field, fullPath, value, isEdit));
        }
      }
    };

    for (const [key, fieldSchema] of Object.entries(schema.properties)) {
      if (!isSchemaObject(fieldSchema)) continue;

      // Skip hidden fields (parity with CustomFields V1)
      if (fieldSchema?.hidden) continue;

      const property = `${propertyPath}${key}`;
      const value = get(formik?.values, property);

      if (isUnitValueShape(fieldSchema)) {
        fieldSchema.objectType = "UnitValue";
        elements.push(renderObjectType(fieldSchema, property, isEdit));
        continue;
      }

      if (fieldSchema.type === "object" && fieldSchema.properties) {
        walkObject(fieldSchema, property);
        continue;
      }

      if (fieldSchema.objectType) {
        elements.push(renderObjectType(fieldSchema, property, isEdit));
        continue;
      }

      elements.push(renderPrimitive(fieldSchema, property, value, isEdit));
    }

    return elements;
  }, [schema?.properties, formik?.values, hasFieldPermissions]);

  return schema && formElements.length ? (
    <>
      {displayHeader && <Typography sx={customHeaderStyles(theme)}>custom fields</Typography>}
      {formElements}
    </>
  ) : null;
};

const checkDate =
  (
    name: string,
    setFieldError: (field: string, message: string) => void,
    minFieldValue?: number,
    maxFieldValue?: number,
    limit?: number,
    limitErrorMessage?: string
  ) =>
  (date: number): any => {
    if (!isValidDate(new Date(date))) {
      setTimeout(() => {
        setFieldError(name, "Invalid Date");
      }, 0);
    } else if (minFieldValue) {
      if (minFieldValue > date)
        setTimeout(() => {
          setFieldError(name, "End date must be after start date");
        }, 0);
      else if (limit && date - minFieldValue > limit) {
        setTimeout(() => {
          setFieldError(name, limitErrorMessage ?? "Please narrow date range");
        }, 0);
      }
    } else if (maxFieldValue) {
      if (maxFieldValue < date)
        setTimeout(() => {
          setFieldError(name, "Start date must be before end date");
        }, 0);
      else if (limit && maxFieldValue - date > limit) {
        setTimeout(() => {
          setFieldError(name, limitErrorMessage ?? "Please narrow date range");
        }, 0);
      }
    }
    return date;
  };

function isValidDate(d: any): boolean {
  return d instanceof Date && !isNaN(d as any);
}
